import React, { createContext, useState, ReactNode, useEffect, useContext } from 'react';
import { arsipkanAksesPegawaiNonaktif } from '../utils/aksesNonaktif';
import { db } from '../firebase';
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch, orderBy, getDoc, increment, where, serverTimestamp } from 'firebase/firestore';
import { hapusDariSalinanJadwal } from '../utils/sinkronJadwal';
import { useAuth } from '../hooks/useAuth';
import { GuruRecord, NewGuruData } from '../types';

export interface GuruContextType {
    loading: boolean;
    guruRecords: GuruRecord[];
    allGuruRecords: GuruRecord[];
    fetchGuruPaginated: (limitCount?: number, startAfterDoc?: any) => Promise<{ data: GuruRecord[], lastDoc: any }>;
    fetchGuruData: () => Promise<void>;

    addGuru: (data: NewGuruData) => Promise<void>;
    updateGuru: (id: string, data: Partial<NewGuruData>) => Promise<void>;
    deleteGuru: (id: string) => Promise<void>;
    addMultipleGuru: (guruData: NewGuruData[]) => Promise<void>;
    updateMultipleGuru: (updates: { id: string; data: Partial<NewGuruData> }[]) => Promise<void>;
}

/** Field guru yang ikut disalin ke jadwal_pegawai (Supabase) untuk pengingat presensi. */
const JADWAL_FIELDS = ['email', 'groupPresensiId', 'statusKeaktifan', 'divisi'];

export const GuruContext = createContext<GuruContextType | undefined>(undefined);

export const useGuru = () => {
    const context = useContext(GuruContext);
    if (context === undefined) {
        throw new Error('useGuru must be used within a GuruProvider');
    }
    return context;
};

export const GuruProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser } = useAuth();

    const [guruRecords, setGuruRecords] = useState<GuruRecord[]>([]);
    const [allGuruRecords, setAllGuruRecords] = useState<GuruRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasFetchedGuru, setHasFetchedGuru] = useState(false);

    useEffect(() => {
        if (!authUser) {
            setGuruRecords([]);
            setAllGuruRecords([]);
            setLoading(false);
            return;
        }

        // Removed eager loading of all guru data here to improve login performance and reduce Firestore reads.
        // Data should be fetched by the specific component that needs it using fetchGuruPaginated.
        setLoading(false);
    }, [authUser]);



    const fetchGuruData = async () => {
        if (!authUser) return;
        if (hasFetchedGuru && allGuruRecords.length > 0) return;

        try {
            const snapshot = await getDocs(query(collection(db, 'guru'), orderBy('nama', 'asc')));
            const guruData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    tanggalLahir: data.tanggalLahir?.toDate(),
                    tanggalBergabung: data.tanggalBergabung?.toDate(),
                } as GuruRecord;
            });
            setAllGuruRecords(guruData);
            setGuruRecords(guruData.filter(g => g.statusKeaktifan === 'Aktif' || !g.statusKeaktifan));
            setHasFetchedGuru(true);
        } catch (e) {
            console.error('Error fetching all guru', e);
        }
    };

    const fetchGuruPaginated = async (limitCount = 50, startAfterDoc = null): Promise<{ data: GuruRecord[], lastDoc: any }> => {
        try {
            const { limit, startAfter } = await import('firebase/firestore');
            let q = query(collection(db, 'guru'), orderBy('nama', 'asc'), limit(limitCount));

            if (startAfterDoc) {
                q = query(collection(db, 'guru'), orderBy('nama', 'asc'), startAfter(startAfterDoc), limit(limitCount));
            }

            const snapshot = await getDocs(q);
            const lastDoc = snapshot.docs[snapshot.docs.length - 1];

            const guruData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    tanggalLahir: data.tanggalLahir?.toDate(),
                    tanggalBergabung: data.tanggalBergabung?.toDate(),
                } as GuruRecord;
            });

            // Note: Since pagination means we don't have all data, we only update state for the first page
            // to ensure basic app functionality that might still rely on guruRecords state array.
            if (!startAfterDoc) {
                setAllGuruRecords(guruData);
                setGuruRecords(guruData.filter(g => g.statusKeaktifan === 'Aktif' || !g.statusKeaktifan));
            } else {
                setAllGuruRecords(prev => {
                    const newItems = guruData.filter(ng => !prev.some(pg => pg.id === ng.id));
                    return [...prev, ...newItems];
                });
                setGuruRecords(prev => {
                    const activeNew = guruData.filter(g => g.statusKeaktifan === 'Aktif' || !g.statusKeaktifan);
                    const newItems = activeNew.filter(ng => !prev.some(pg => pg.id === ng.id));
                    return [...prev, ...newItems];
                });
            }

            return { data: guruData, lastDoc };
        } catch (e) {
            console.error('Error fetching paginated guru:', e);
            return { data: [], lastDoc: null };
        }
    };

    const addGuru = async (data: NewGuruData) => {
        const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
            if (value !== undefined) acc[key] = value;
            return acc;
        }, {} as any);

        if (cleanData.email) {
            cleanData.email = cleanData.email.toLowerCase().trim();
            const isEmailUsed = allGuruRecords.some(g => g.email?.toLowerCase().trim() === cleanData.email);
            if (isEmailUsed) {
                alert(`Email ${cleanData.email} sudah digunakan oleh pegawai lain.`);
                throw new Error('Email sudah digunakan');
            }
        }

        const docRef = await addDoc(collection(db, 'guru'), { ...cleanData, jadwal_diperbarui: serverTimestamp() });

        if (cleanData.groupPresensiId) {
            const batch = writeBatch(db);
            const memberRef = doc(db, 'grup_presensi', cleanData.groupPresensiId, 'anggota', docRef.id);
            batch.set(memberRef, {
                id: docRef.id,
                nama: cleanData.nama,
                niy: cleanData.niy || '',
                foto: cleanData.foto || null,
                unit: cleanData.divisi || null,
                jabatan: null,
                addedAt: new Date()
            });
            const groupRef = doc(db, 'grup_presensi', cleanData.groupPresensiId);
            batch.update(groupRef, { pegawai_count: increment(1) });
            await batch.commit();
        }
    };

    const updateGuru = async (id: string, data: Partial<NewGuruData>) => {
        const guruRef = doc(db, 'guru', id);
        const guruSnap = await getDoc(guruRef);
        const oldData = guruSnap.exists() ? guruSnap.data() as GuruRecord : null;

        const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
            if (value !== undefined) acc[key] = value;
            return acc;
        }, {} as any);

        if (cleanData.email) {
            cleanData.email = cleanData.email.toLowerCase().trim();
            const isEmailUsed = allGuruRecords.some(g => g.id !== id && g.email?.toLowerCase().trim() === cleanData.email);
            if (isEmailUsed) {
                alert(`Email ${cleanData.email} sudah digunakan oleh pegawai lain.`);
                throw new Error('Email sudah digunakan');
            }
        }

        // Penanda untuk salinan jadwal di Supabase (pengingat presensi) bila
        // data yang dipakai jadwal berubah. Tidak ikut tersimpan di state lokal.
        const jadwalBerubah = JADWAL_FIELDS.some(f => f in cleanData && (cleanData[f] ?? null) !== ((oldData as any)?.[f] ?? null));
        const batch = writeBatch(db);
        batch.update(guruRef, jadwalBerubah ? { ...cleanData, jadwal_diperbarui: serverTimestamp() } : cleanData);
        let memberDisplaySync: { ref: ReturnType<typeof doc>; data: Record<string, any> } | null = null;

        if (oldData && data.nama && data.nama !== oldData.nama) {
            const oldName = oldData.nama;
            const newName = data.nama;

            const qJadwal = query(collection(db, 'jadwal_pelajaran'), where('guru', '==', oldName));
            const jadwalSnap = await getDocs(qJadwal);
            jadwalSnap.docs.forEach(doc => batch.update(doc.ref, { guru: newName }));

            const qKelas = query(collection(db, 'kelas'), where('waliKelas', '==', oldName));
            const kelasSnap = await getDocs(qKelas);
            kelasSnap.docs.forEach(doc => batch.update(doc.ref, { waliKelas: newName }));

            const qSantriWK = query(collection(db, 'santri'), where('waliKelas', '==', oldName), where('status', '==', 'Aktif'));
            const santriWKSnap = await getDocs(qSantriWK);
            santriWKSnap.docs.forEach(doc => batch.update(doc.ref, { waliKelas: newName }));

            const qMurobbi = query(collection(db, 'santri'), where('murobbi', '==', oldName), where('status', '==', 'Aktif'));
            const murobbiSnap = await getDocs(qMurobbi);
            murobbiSnap.docs.forEach(doc => batch.update(doc.ref, { murobbi: newName }));

            // Fallback for name change if group hasn't changed
            if (!('groupPresensiId' in cleanData) || cleanData.groupPresensiId === oldData.groupPresensiId) {
                const groupId = oldData.groupPresensiId;
                if (groupId) {
                    const memberRef = doc(db, 'grup_presensi', groupId, 'anggota', id);
                    // batch.update pada dokumen yang tidak ada → SELURUH batch gagal.
                    if ((await getDoc(memberRef)).exists()) {
                        batch.update(memberRef, { nama: newName });
                    }
                }
            }
        }

        // Handle Group Presensi Change or Divisi Change for Group Member
        if (oldData) {
            const oldGroupId = oldData.groupPresensiId;
            const newGroupId = 'groupPresensiId' in cleanData ? cleanData.groupPresensiId : oldData.groupPresensiId;

            const oldDivisi = oldData.divisi || '';
            const newDivisi = ('divisi' in cleanData ? cleanData.divisi : oldData.divisi) || '';

            // Normalisasi: field kosong di database (undefined) dan '' di form
            // dianggap SAMA. Dulu undefined !== '' dianggap "NIY berubah" dan
            // memicu update anggota grup presensi yang tidak perlu.
            const oldNiy = oldData.niy || '';
            const newNiy = ('niy' in cleanData ? cleanData.niy : oldData.niy) || '';

            const nameToSave = data.nama || oldData.nama;

            // Scenario 1: Group Changed
            if (oldGroupId !== newGroupId) {
                // Remove from old group
                if (oldGroupId) {
                    const oldMemberRef = doc(db, 'grup_presensi', oldGroupId, 'anggota', id);
                    batch.delete(oldMemberRef);
                    const oldGroupRef = doc(db, 'grup_presensi', oldGroupId);
                    batch.update(oldGroupRef, { pegawai_count: increment(-1) });
                }

                // Add to new group
                if (newGroupId) {
                    const newMemberRef = doc(db, 'grup_presensi', newGroupId, 'anggota', id);
                    batch.set(newMemberRef, {
                        id: id,
                        nama: nameToSave,
                        niy: newNiy || '',
                        foto: oldData.foto || null,
                        unit: newDivisi || null,
                        jabatan: null,
                        addedAt: new Date()
                    });
                    const newGroupRef = doc(db, 'grup_presensi', newGroupId);
                    batch.update(newGroupRef, { pegawai_count: increment(1) });
                }
            }
            // Scenario 2: Group is same, but properties displayed in Member List changed
            // → disinkronkan SETELAH batch utama (lihat di bawah).
            else if (newGroupId && (oldDivisi !== newDivisi || oldNiy !== newNiy)) {
                memberDisplaySync = {
                    ref: doc(db, 'grup_presensi', newGroupId, 'anggota', id),
                    data: { unit: newDivisi || null, niy: newNiy || '' }
                };
            }
        }

        await batch.commit();

        if (cleanData.statusKeaktifan === 'Nonaktif' && oldData?.statusKeaktifan !== 'Nonaktif') {
            arsipkanAksesPegawaiNonaktif([id]);
        }

        // Sinkronisasi tampilan daftar anggota grup presensi (NIY / unit) dibuat
        // TERPISAH dan best-effort: ini hanya salinan untuk tampilan. Kalau
        // gagal (dokumen anggota tidak ada / pegawai tidak berhak menulis grup
        // presensi), data pribadi pegawai TETAP tersimpan.
        if (memberDisplaySync) {
            try {
                if ((await getDoc(memberDisplaySync.ref)).exists()) {
                    await updateDoc(memberDisplaySync.ref, memberDisplaySync.data);
                }
            } catch (err) {
                console.warn('Sinkron NIY/unit ke anggota grup presensi dilewati:', err);
            }
        }

        if (oldData) {
            // oldData berasal langsung dari getDoc → tanggal masih berupa
            // Firestore Timestamp & tanpa `id`. Samakan dengan bentuk data
            // hasil fetch (Timestamp → Date, sertakan id). Tanpa ini, field
            // tanggal yang TIDAK diubah tetap Timestamp → `new Date(Timestamp)`
            // = Invalid Date → halaman crash ("RangeError: Invalid time value").
            const normalizedOld = Object.fromEntries(
                Object.entries(oldData).map(([k, v]) => [k, v && typeof (v as any).toDate === 'function' ? (v as any).toDate() : v])
            ) as GuruRecord;
            const updatedGuru = { ...normalizedOld, ...cleanData, id } as GuruRecord;
            setAllGuruRecords(prev => prev.map(g => g.id === id ? updatedGuru : g));
            setGuruRecords(prev => {
                if (cleanData.statusKeaktifan && cleanData.statusKeaktifan !== 'Aktif') {
                    return prev.filter(g => g.id !== id);
                }
                if (cleanData.statusKeaktifan === 'Aktif' && !prev.find(g => g.id === id)) {
                    return [...prev, updatedGuru].sort((a, b) => a.nama.localeCompare(b.nama));
                }
                return prev.map(g => g.id === id ? updatedGuru : g);
            });
        }
    };

    const deleteGuru = async (id: string) => {
        await deleteDoc(doc(db, 'guru', id));
        hapusDariSalinanJadwal({ hapusGuru: id });
    };

    const addMultipleGuru = async (guruData: NewGuruData[]) => {
        const batch = writeBatch(db);
        const groupCounts: Record<string, number> = {};

        guruData.forEach(data => {
            const newDocRef = doc(collection(db, 'guru'));
            batch.set(newDocRef, { ...data, jadwal_diperbarui: serverTimestamp() });

            if (data.groupPresensiId) {
                const memberRef = doc(db, 'grup_presensi', data.groupPresensiId, 'anggota', newDocRef.id);
                batch.set(memberRef, {
                    id: newDocRef.id,
                    nama: data.nama,
                    niy: data.niy || '',
                    foto: data.foto || null,
                    unit: data.divisi || null,
                    addedAt: new Date()
                });
                groupCounts[data.groupPresensiId] = (groupCounts[data.groupPresensiId] || 0) + 1;
            }
        });

        Object.entries(groupCounts).forEach(([groupId, count]) => {
            const groupRef = doc(db, 'grup_presensi', groupId);
            batch.update(groupRef, { pegawai_count: increment(count) });
        });

        await batch.commit();
    };

    const updateMultipleGuru = async (updates: { id: string; data: Partial<NewGuruData> }[]) => {
        const batch = writeBatch(db);
        updates.forEach(({ id, data }) => {
            const guruRef = doc(db, 'guru', id);
            const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
                if (value !== undefined) acc[key] = value;
                return acc;
            }, {} as any);
            if (JADWAL_FIELDS.some(f => f in cleanData)) cleanData.jadwal_diperbarui = serverTimestamp();
            batch.update(guruRef, cleanData);
        });

        await batch.commit();

        arsipkanAksesPegawaiNonaktif(updates.filter(u => u.data.statusKeaktifan === 'Nonaktif').map(u => u.id));

        // Refresh local state without making a new network request
        setAllGuruRecords(prev => prev.map(g => {
            const update = updates.find(u => u.id === g.id);
            return update ? { ...g, ...update.data } as GuruRecord : g;
        }));

        setGuruRecords(prev => {
             // For simplicity, we filter the all records again instead of doing complex patch logic for statusKeaktifan changes
             const updatedAll = allGuruRecords.map(g => {
                const update = updates.find(u => u.id === g.id);
                return update ? { ...g, ...update.data } as GuruRecord : g;
            });
            return updatedAll.filter(g => g.statusKeaktifan === 'Aktif' || !g.statusKeaktifan);
        });
    };

    return (
        <GuruContext.Provider value={{
            loading,
            guruRecords, allGuruRecords, fetchGuruPaginated, fetchGuruData,
            addGuru, updateGuru, deleteGuru, addMultipleGuru, updateMultipleGuru
        }}>
            {children}
        </GuruContext.Provider>
    );
};
